import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Users, Calendar, Clock, TrendingUp, MapPin, Zap,
  Briefcase, BarChart3, Search, Download, RefreshCw, ChevronRight
} from 'lucide-react';

/* ─── DARK DATEPICKER OVERRIDE ───────────────────────────────── */
const DP_STYLES = `
  .ta-dp-wrap .react-datepicker-wrapper,
  .ta-dp-wrap .react-datepicker__input-container { width:100%; }

  .ta-dp-wrap .react-datepicker__input-container input {
    width:100%; padding:11px 14px 11px 38px;
    background:var(--nd-w04);
    border:1px solid var(--nd-w08);
    border-radius:10px; color:var(--nd-text); font-size:13px;
    font-family:'DM Sans',sans-serif; font-weight:500;
    outline:none; cursor:pointer; transition:border-color .2s,box-shadow .2s;
    color-scheme:dark;
  }
  .ta-dp-wrap .react-datepicker__input-container input:focus {
    border-color:rgba(34,211,165,0.38);
    box-shadow:0 0 0 3px rgba(34,211,165,0.07);
  }
  .react-datepicker {
    background:var(--nd-surface) !important; border:1px solid var(--nd-w08) !important;
    border-radius:12px !important; box-shadow:0 16px 40px rgba(0,0,0,0.5) !important;
    font-family:'DM Sans',sans-serif !important;
  }
  .react-datepicker__header {
    background:var(--nd-surface-2) !important; border-bottom:1px solid var(--nd-w08) !important;
    border-radius:12px 12px 0 0 !important;
  }
  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker-time__header { color:var(--nd-text) !important; font-size:12px !important; }
  .react-datepicker__day { color:#94A3B8 !important; border-radius:6px !important; }
  .react-datepicker__day:hover { background:rgba(34,211,165,0.12) !important; color:#22D3A5 !important; }
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background:linear-gradient(135deg,#3B82F6,#22D3A5) !important;
    color:var(--nd-text-strong) !important; font-weight:700 !important;
  }
  .react-datepicker__day--outside-month { color:var(--nd-w20) !important; }
  .react-datepicker__navigation-icon::before { border-color:var(--nd-tx35) !important; }
  .react-datepicker__triangle { display:none !important; }
  .react-datepicker__day--today { font-weight:700; color:#3B82F6 !important; }
`;

/* ─── COMPONENT STYLES ───────────────────────────────────────── */
const S = {
  page: {
    padding: '14px 20px',
    fontFamily: "'DM Sans', sans-serif",
  },

  /* header */
  headerCard: {
    background: 'var(--nd-surface)',
    border: '1px solid var(--nd-w06)',
    borderRadius: '14px',
    padding: '20px 24px 18px',
    marginBottom: '20px',
  },
  breadcrumb: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: 'var(--nd-tx25)',
    marginBottom: '10px',
  },
  breadcrumbSep: { color: 'var(--nd-w12)' },
  breadcrumbActive: { color: 'rgba(34,211,165,0.55)' },
  titleRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap',
    marginBottom: '14px',
  },
  pageTitle: {
    fontSize: '26px', fontWeight: '800', color: 'var(--nd-tx85)',
    letterSpacing: '-0.03em', lineHeight: 1.1, margin: 0,
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  pageSub: {
    fontSize: '12px', color: 'var(--nd-tx30)',
    marginTop: '5px', fontWeight: '400',
  },
  foCountBadge: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
  },
  foCountNum: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '36px', fontWeight: '700', lineHeight: 1,
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  foCountLabel: {
    fontSize: '11px', color: 'var(--nd-tx25)',
    fontWeight: '600', letterSpacing: '0.08em',
    textTransform: 'uppercase', marginTop: '2px',
  },
  gradLine: {
    height: '2px', borderRadius: '2px',
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)',
    marginTop: '16px',
  },

  /* surface card */
  card: {
    background: 'var(--nd-surface)',
    border: '1px solid var(--nd-w06)',
    borderRadius: '14px',
    padding: '20px 24px',
    marginBottom: '18px',
  },
  cardTitle: {
    display: 'flex', alignItems: 'center', gap: '9px',
    fontSize: '13px', fontWeight: '700', color: 'var(--nd-tx70)',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    marginBottom: '18px',
  },

  /* filter grid */
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '14px',
    alignItems: 'end',
  },
  label: {
    display: 'flex', alignItems: 'center', gap: '7px',
    fontSize: '10px', fontWeight: '700', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: 'var(--nd-tx30)',
    marginBottom: '8px',
  },
  select: {
    width: '100%', padding: '11px 14px',
    background: 'var(--nd-w04)',
    border: '1px solid var(--nd-w08)',
    borderRadius: '10px', color: 'var(--nd-text)',
    fontSize: '13px', fontWeight: '500',
    outline: 'none', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'border-color .2s, box-shadow .2s',
    colorScheme: 'dark',
  },

  /* action button */
  btn: {
    padding: '11px 22px',
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    color: 'var(--nd-text-strong)', border: 'none', borderRadius: '10px',
    fontWeight: '700', fontSize: '13px', letterSpacing: '0.06em',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: '8px', width: '100%',
    fontFamily: "'DM Sans', sans-serif",
    boxShadow: '0 4px 18px rgba(34,211,165,0.18)',
    transition: 'opacity .2s, transform .15s',
  },
  btnDisabled: {
    opacity: 0.45, cursor: 'not-allowed',
  },

  /* selected FO strip */
  foStrip: {
    marginTop: '16px', padding: '14px 16px',
    background: 'rgba(34,211,165,0.04)',
    border: '1px solid rgba(34,211,165,0.10)',
    borderRadius: '10px', display: 'flex',
    alignItems: 'center', gap: '14px',
  },
  foName: { fontWeight: '700', color: 'var(--nd-text)', fontSize: '14px' },
  foRole: { fontSize: '11px', color: 'var(--nd-tx30)', marginTop: '2px' },

  /* error */
  errorBox: {
    background: 'rgba(248,113,113,0.06)',
    border: '1px solid rgba(248,113,113,0.18)',
    borderRadius: '10px', padding: '13px 16px',
    color: '#F87171', fontWeight: '600', fontSize: '13px',
    marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px',
  },

  /* stat cards grid */
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '14px', marginBottom: '18px',
  },
  statCard: {
    background: 'var(--nd-surface)',
    border: '1px solid var(--nd-w06)',
    borderRadius: '12px', padding: '18px 20px',
    transition: 'border-color .2s, transform .2s',
    cursor: 'default',
  },
  statIcon: {
    width: '40px', height: '40px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '14px',
  },
  statLabel: {
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.10em',
    textTransform: 'uppercase', color: 'var(--nd-tx30)',
    marginBottom: '6px',
  },
  statValue: {
    fontSize: '30px', fontWeight: '800', lineHeight: 1,
    fontFamily: "'DM Mono', monospace",
    background: 'linear-gradient(90deg,var(--nd-text),#94A3B8)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  statUnit: {
    fontSize: '14px', fontWeight: '500',
    color: 'var(--nd-tx30)', marginLeft: '4px',
    WebkitTextFillColor: 'var(--nd-tx30)',
  },

  /* table card */
  tableCard: {
    background: 'var(--nd-surface)',
    border: '1px solid var(--nd-w06)',
    borderRadius: '14px', overflow: 'hidden',
    marginBottom: '18px',
  },
  tableHead: {
    padding: '16px 24px',
    borderBottom: '1px solid var(--nd-w06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableTitle: {
    fontSize: '13px', fontWeight: '700',
    color: 'var(--nd-tx70)', letterSpacing: '0.06em',
    textTransform: 'uppercase', margin: 0,
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  exportBtn: {
    padding: '7px 14px',
    background: 'var(--nd-w04)',
    border: '1px solid var(--nd-w08)',
    borderRadius: '8px', color: 'var(--nd-tx45)',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'background .2s, color .2s',
  },
  th: {
    padding: '12px 16px',
    fontSize: '10px', fontWeight: '700',
    color: 'var(--nd-tx25)',
    textTransform: 'uppercase', letterSpacing: '0.12em',
    textAlign: 'left', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--nd-w05)',
    background: 'var(--nd-w02)',
  },
  td: {
    padding: '14px 16px',
    fontSize: '13px', color: '#94A3B8',
    borderBottom: '1px solid var(--nd-w04)',
    whiteSpace: 'nowrap',
  },

  /* pill badges */
  pillAmber: {
    background: 'rgba(251,191,36,0.10)',
    border: '1px solid rgba(251,191,36,0.18)',
    color: '#FBBF24', padding: '3px 10px',
    borderRadius: '6px', fontSize: '12px', fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  pillGreen: {
    background: 'rgba(52,211,153,0.09)',
    border: '1px solid rgba(52,211,153,0.18)',
    color: '#34D399', padding: '3px 10px',
    borderRadius: '6px', fontSize: '12px', fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  pillBlue: {
    background: 'rgba(59,130,246,0.10)',
    border: '1px solid rgba(59,130,246,0.18)',
    color: '#60A5FA', padding: '3px 10px',
    borderRadius: '6px', fontSize: '12px', fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  jobId: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '13px', fontWeight: '700',
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },

  /* job type toggle */
  typeToggle: {
    display: 'inline-flex', gap: '4px', padding: '4px',
    background: 'var(--nd-w04)', border: '1px solid var(--nd-w08)',
    borderRadius: '10px',
  },
  typeBtn: {
    padding: '7px 16px', border: 'none', borderRadius: '7px',
    background: 'transparent', color: 'var(--nd-tx45)',
    fontSize: '12px', fontWeight: '700', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", transition: 'background .2s, color .2s',
  },
  typeBtnActive: {
    background: 'linear-gradient(90deg,#3B82F6,#22D3A5)',
    color: 'var(--nd-text-strong)',
  },
  jobLink: { textDecoration: 'none', cursor: 'pointer' },

  /* empty state */
  emptyState: {
    background: 'var(--nd-surface)',
    border: '1px solid var(--nd-w06)',
    borderRadius: '14px', padding: '60px 20px',
    textAlign: 'center', marginBottom: '18px',
  },
  emptyIcon: {
    width: '60px', height: '60px', borderRadius: '16px',
    background: 'rgba(59,130,246,0.07)',
    border: '1px solid rgba(59,130,246,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 16px',
  },
  emptyTitle: {
    fontSize: '15px', fontWeight: '700',
    color: 'var(--nd-tx45)', margin: '0 0 6px',
  },
  emptySub: {
    fontSize: '13px', color: 'var(--nd-tx22)',
  },
};

/* ─── AVATAR ─────────────────────────────────────────────────── */
function TeamMemberAvatar({ name, size = 40 }) {
  const gradients = [
    ['#3B82F6','#22D3A5'], ['#8B5CF6','#3B82F6'], ['#22D3A5','#3B82F6'],
    ['#F472B6','#8B5CF6'], ['#FBBF24','#F472B6'], ['#34D399','#22D3A5'],
    ['#60A5FA','#A78BFA'], ['#F87171','#FBBF24'],
  ];
  const hash = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const [c1, c2] = gradients[hash % gradients.length];
  const initials = name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, minWidth: size,
      borderRadius: '10px',
      background: `linear-gradient(135deg,${c1},${c2})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--nd-text-strong)', fontWeight: '800', fontSize: size * 0.33,
      letterSpacing: '0.5px', boxShadow: `0 4px 14px rgba(0,0,0,0.35)`,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {initials}
    </div>
  );
}

/* ─── STAT CARD ──────────────────────────────────────────────── */
function StatCard({ icon, label, value, unit, accentColor }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        ...S.statCard,
        borderColor: hov ? `${accentColor}30` : 'var(--nd-w06)',
        transform: hov ? 'translateY(-3px)' : 'none',
        boxShadow: hov ? `0 8px 28px rgba(0,0,0,0.25), 0 0 0 1px ${accentColor}20` : 'none',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{ ...S.statIcon, background: `${accentColor}12`, border: `1px solid ${accentColor}20` }}>
        {icon}
      </div>
      <div style={S.statLabel}>{label}</div>
      <div style={{ ...S.statValue }}>
        {value}
        {unit && <span style={S.statUnit}>{unit}</span>}
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────── */
// new_customers.client_type: 1 = Residential, 2 = Commercial
const JOB_TYPES = [
  { key: 'all',         label: 'All' },
  { key: 'commercial',  label: 'Commercial' },
  { key: 'residential', label: 'Residential' },
];

const jobTypeOf = (job) => {
  const t = Number(job.client_type);
  if (t === 1) return 'residential';
  if (t === 2) return 'commercial';
  return null;
};

// Filters + loaded results survive opening a job and pressing Back (component unmounts on navigate)
const SAVED_KEY = 'team_analytics_state';

const loadSaved = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SAVED_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const savedDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d : new Date();
};

const AdminTeamAnalytics = () => {
  const navigate = useNavigate();
  const [saved] = useState(loadSaved);
  const [jobType, setJobType]         = useState(saved.jobType || 'all');
  const [foList, setFoList]           = useState([]);
  const [selectedFo, setSelectedFo]   = useState(saved.selectedFo || '');
  const [startDate, setStartDate]     = useState(() => savedDate(saved.startDate));
  const [endDate, setEndDate]         = useState(() => savedDate(saved.endDate));
  const [performanceData, setPerformanceData] = useState(saved.performanceData || []);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const [selectedCities, setSelectedCities] = useState(saved.selectedCities || []);
  const [selectedOffices, setSelectedOffices] = useState(saved.selectedOffices || []);
  const [selectedSupervisors, setSelectedSupervisors] = useState(saved.selectedSupervisors || []);

  useEffect(() => {
    try {
      sessionStorage.setItem(SAVED_KEY, JSON.stringify({
        jobType, selectedFo, performanceData,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        selectedCities, selectedOffices, selectedSupervisors,
      }));
    } catch (e) { /* storage unavailable or full — page still works, just won't restore */ }
  }, [jobType, selectedFo, startDate, endDate, performanceData, selectedCities, selectedOffices, selectedSupervisors]);

  const uniqueCities = [...new Set(foList.map(fo => fo.city).filter(Boolean))];
  const uniqueOffices = [...new Set(foList.map(fo => fo.office).filter(Boolean))];
  const uniqueSupervisors = [...new Set(foList.map(fo => fo.supervisor).filter(Boolean))];

  const filteredFoList = foList.filter(fo => {
    if (selectedCities.length > 0 && (!fo.city || !selectedCities.includes(fo.city))) return false;
    if (selectedOffices.length > 0 && (!fo.office || !selectedOffices.includes(fo.office))) return false;
    if (selectedSupervisors.length > 0 && (!fo.supervisor || !selectedSupervisors.includes(fo.supervisor))) return false;
    return true;
  });

  const toggleFilter = (setter, list, val) => {
    if (list.includes(val)) setter(list.filter(item => item !== val));
    else setter([...list, val]);
  };

  const fetchFoList = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/team-members`);
      setFoList(res.data);
    } catch (err) {
      console.error('Error fetching FOs', err);
      setError('Failed to load team members');
    }
  };

  const fetchPerformance = async () => {
    const targetFos = selectedFo ? [selectedFo] : filteredFoList.map(fo => fo.name);
    if (targetFos.length === 0) { alert('No field operators match the selected criteria.'); return; }
    
    setLoading(true); setError('');
    const start = startDate.toISOString().split('T')[0];
    const end   = endDate.toISOString().split('T')[0];
    try {
      let combinedData = [];
      const promises = targetFos.map(foName => 
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/fo-performance/${encodeURIComponent(foName)}?start=${start}&end=${end}`)
      );
      const results = await Promise.all(promises);
      results.forEach(res => {
        if (res.data && Array.isArray(res.data)) combinedData = [...combinedData, ...res.data];
      });
      setPerformanceData(combinedData);
      console.log('Raw performance data sample:', combinedData[0]);
    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError('Failed to load performance data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchFoList(); }, []);

  const visibleJobs = jobType === 'all'
    ? performanceData
    : performanceData.filter(j => jobTypeOf(j) === jobType);

  const calculateAvg = (field) => {
    const vals = visibleJobs.map(d => d[field]).filter(n => n != null);
    if (!vals.length) return '—';
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  };

  const totalSystemSize = () => {
    const vals = visibleJobs.map(d => parseFloat(d.system_size)).filter(n => !isNaN(n));
    if (!vals.length) return '—';
    return vals.reduce((a, b) => a + b, 0).toFixed(1);
  };

  const selectedFoData = foList.find(fo => fo.name === selectedFo);

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
        ${DP_STYLES}
        .ta-select:focus { border-color:rgba(34,211,165,0.38)!important; box-shadow:0 0 0 3px rgba(34,211,165,0.07)!important; }
        .ta-select option { background:var(--nd-surface-2); color:var(--nd-text); }
        .ta-export-btn:hover { background:var(--nd-w08)!important; color:var(--nd-tx70)!important; }
        .ta-row:hover td { background:var(--nd-w02)!important; }
        .ta-btn-load:hover:not(:disabled) { opacity:0.88; transform:translateY(-1px); }
        @keyframes ta-spin { to { transform:rotate(360deg); } }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────── */}
      <div style={S.headerCard}>
        <div style={S.breadcrumb}>
          <span>Operations</span>
          <ChevronRight size={12} style={S.breadcrumbSep}/>
          <span style={S.breadcrumbActive}>Team Analytics</span>
        </div>
        <div style={S.titleRow}>
          <div>
            <div style={S.pageTitle}>
              <BarChart3 size={24} color="#3B82F6"/>
              Team Performance Analytics
            </div>
            <div style={S.pageSub}>Track and analyze field operator job performance metrics</div>
          </div>
          <div style={S.foCountBadge}>
            <div style={S.foCountNum}>{filteredFoList.length}</div>
            <div style={S.foCountLabel}>Matching FOs</div>
          </div>
        </div>
        <div style={S.gradLine}/>
      </div>

      {/* ── FILTERS ────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardTitle}>
          <Search size={14} color="#3B82F6"/>
          Select Criteria
        </div>

        {/* Dynamic Multi-Select Checkbox Filters */}
        <div style={{ padding: '0 10px 18px', borderBottom: '1px solid var(--nd-w06)', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {uniqueCities.length > 0 && (
            <div>
              <div style={{ ...S.label, marginBottom: '6px' }}>Filter by City:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {uniqueCities.map(city => (
                  <label key={city} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--nd-tx85)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedCities.includes(city)} onChange={() => toggleFilter(setSelectedCities, selectedCities, city)} style={{ cursor: 'pointer' }} />
                    {city}
                  </label>
                ))}
              </div>
            </div>
          )}
          {uniqueOffices.length > 0 && (
            <div>
              <div style={{ ...S.label, marginBottom: '6px' }}>Filter by Office:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {uniqueOffices.map(office => (
                  <label key={office} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--nd-tx85)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedOffices.includes(office)} onChange={() => toggleFilter(setSelectedOffices, selectedOffices, office)} style={{ cursor: 'pointer' }} />
                    {office}
                  </label>
                ))}
              </div>
            </div>
          )}
          {uniqueSupervisors.length > 0 && (
            <div>
              <div style={{ ...S.label, marginBottom: '6px' }}>Filter by Supervisor:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {uniqueSupervisors.map(sup => (
                  <label key={sup} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--nd-tx85)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedSupervisors.includes(sup)} onChange={() => toggleFilter(setSelectedSupervisors, selectedSupervisors, sup)} style={{ cursor: 'pointer' }} />
                    {sup}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '0 10px 18px', borderBottom: '1px solid var(--nd-w06)', marginBottom: '18px' }}>
          <div style={{ ...S.label, marginBottom: '6px' }}>Job Type:</div>
          <div style={S.typeToggle}>
            {JOB_TYPES.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setJobType(t.key)}
                style={{ ...S.typeBtn, ...(jobType === t.key ? S.typeBtnActive : {}) }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div style={S.filterGrid}>
          {/* Field Operator Select */}
          <div>
            <div style={S.label}><Users size={13} color="var(--nd-tx30)"/>Field Operator</div>
            <select
              className="ta-select"
              value={selectedFo}
              onChange={e => setSelectedFo(e.target.value)}
              style={S.select}
            >
              <option value="">All Filtered FOs ({filteredFoList.length})</option>
              {filteredFoList.map(fo => (
                <option key={fo.name} value={fo.name}>{fo.name}</option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <div style={S.label}><Calendar size={13} color="var(--nd-tx30)"/>Start Date</div>
            <div className="ta-dp-wrap" style={{ position: 'relative' }}>
              <Calendar size={14} color="var(--nd-tx22)" style={{
                position: 'absolute', left: '12px', top: '50%',
                transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 2,
              }}/>
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                dateFormat="MMM dd, yyyy"
                className="custom-datepicker"
              />
            </div>
          </div>

          {/* End Date */}
          <div>
            <div style={S.label}><Calendar size={13} color="var(--nd-tx30)"/>End Date</div>
            <div className="ta-dp-wrap" style={{ position: 'relative' }}>
              <Calendar size={14} color="var(--nd-tx22)" style={{
                position: 'absolute', left: '12px', top: '50%',
                transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 2,
              }}/>
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                dateFormat="MMM dd, yyyy"
                className="custom-datepicker"
              />
            </div>
          </div>

          {/* Load Button */}
          <div>
            <div style={{ ...S.label, opacity: 0 }}>action</div>
            <button
              className="ta-btn-load"
              onClick={fetchPerformance}
              disabled={loading}
              style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
            >
              {loading
                ? <><RefreshCw size={16} style={{ animation: 'ta-spin 1s linear infinite' }}/>Loading…</>
                : <><TrendingUp size={16}/>Load Performance</>
              }
            </button>
          </div>
        </div>

        {/* Selected FO strip */}
        {selectedFo && selectedFoData && (
          <div style={S.foStrip}>
            <TeamMemberAvatar name={selectedFo} size={44}/>
            <div>
              <div style={S.foName}>{selectedFo}</div>
              <div style={S.foRole}>Selected Field Operator</div>
            </div>
          </div>
        )}
      </div>

      {/* ── ERROR ──────────────────────────────────────── */}
      {error && (
        <div style={S.errorBox}>
          <span>⚠</span>{error}
        </div>
      )}

      {/* ── PERFORMANCE DATA ───────────────────────────── */}
      {performanceData.length > 0 && (
        <>
          {/* Summary Stats */}
          <div style={S.statsGrid}>
            <StatCard icon={<Briefcase size={20} color="#3B82F6"/>}    label="Total Jobs Completed"    value={visibleJobs.length}                             unit=""    accentColor="#3B82F6"/>
            <StatCard icon={<Clock size={20} color="#FBBF24"/>}        label="Avg Start → Before"      value={calculateAvg('start_to_before_minutes')}             unit="min" accentColor="#FBBF24"/>
            <StatCard icon={<Clock size={20} color="#34D399"/>}        label="Avg Before → Complete"   value={calculateAvg('before_to_complete_minutes')}          unit="min" accentColor="#34D399"/>
            <StatCard icon={<Clock size={20} color="#A78BFA"/>}        label="Avg Total Time"          value={calculateAvg('total_duration_minutes')}              unit="min" accentColor="#A78BFA"/>
            <StatCard icon={<Zap size={20} color="#FBBF24"/>}          label="Total System Size"       value={totalSystemSize()}                                    unit="kW"  accentColor="#FBBF24"/>
          </div>

          {/* Job Table */}
          <div style={S.tableCard}>
            <div style={S.tableHead}>
              <div style={S.tableTitle}>
                <Briefcase size={15} color="#3B82F6"/>
                Job Details
              </div>
              <button
                className="ta-export-btn"
                style={S.exportBtn}
              >
                <Download size={14}/>Export
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Job ID','Type','Date','Start → Before','Before → Complete','Total Time','System Size','Location'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleJobs.length === 0 && (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', padding: '28px' }}>No jobs match this job type</td></tr>
                  )}
                  {visibleJobs.map((job) => (
                    <tr
                      key={job.job_id}
                      className="ta-row"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/job-details/${job.job_id}`)}
                    >
                      <td style={S.td}>
                        <a
                          href={`/admin/job-details/${job.job_id}`}
                          style={S.jobLink}
                          onClick={e => { if (!e.ctrlKey && !e.metaKey && e.button === 0) e.preventDefault(); else e.stopPropagation(); }}
                        >
                          <span style={S.jobId}>#{job.job_id}</span>
                        </a>
                      </td>
                      <td style={S.td}>
                        {jobTypeOf(job) === 'commercial' && <span style={S.pillBlue}>Commercial</span>}
                        {jobTypeOf(job) === 'residential' && <span style={S.pillGreen}>Residential</span>}
                        {!jobTypeOf(job) && '—'}
                      </td>
                      <td style={{ ...S.td, color: 'var(--nd-text)' }}>
                        {new Date(job.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={S.td}>
                        {job.before_uploaded_at == null ? (
                          <span style={{
                            background: 'rgba(248,113,113,0.08)',
                            border: '1px solid rgba(248,113,113,0.18)',
                            color: '#F87171', padding: '3px 10px',
                            borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                            fontFamily: "'DM Mono',monospace"
                          }}>no before photo</span>
                        ) : (
                          <span style={S.pillAmber}>{job.start_to_before_minutes} min</span>
                        )}
                      </td>
                      <td style={S.td}>
                        <span style={S.pillGreen}>{job.before_to_complete_minutes ?? '—'} min</span>
                      </td>
                      <td style={S.td}>
                        <span style={S.pillBlue}>{job.total_duration_minutes ?? '—'} min</span>
                      </td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Zap size={13} color="#FBBF24"/>
                          <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: '600', color: 'var(--nd-text)' }}>
                            {job.system_size != null ? `${job.system_size} kW` : '—'}
                          </span>
                        </div>
                      </td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <MapPin size={13} color="var(--nd-tx30)"/>
                          {job.client_city}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── EMPTY STATE ────────────────────────────────── */}
      {!loading && performanceData.length === 0 && !error && (
        <div style={S.emptyState}>
          <div style={S.emptyIcon}>
            <BarChart3 size={26} color="#3B82F6"/>
          </div>
          <p style={S.emptyTitle}>No Performance Data</p>
          <p style={S.emptySub}>Select a field operator and date range to view analytics</p>
        </div>
      )}
    </div>
  );
};

export default AdminTeamAnalytics;